import { useState, useEffect } from "react";
import axios from "axios";
import { hapticTap, hapticSuccess, hapticError } from "../utils/haptics";
import { soundWin } from "../utils/sound";
import { ExecutionConfirmModal } from "./SwipeCards";
import { PLChart, ZiProjectionPanel } from "./OptionsTradingFlow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fN = (v, d = 2) => (v == null || isNaN(v)) ? "—" : Number(v).toFixed(d);

// Parse OSI option symbol → { ticker, strike, side, expDate }
function parseOCC(sym) {
  if (!sym) return null;
  const m = sym.match(/^([A-Z]+)(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/);
  if (!m) return null;
  const [, ticker, yy, mm, dd, cp, rawStrike] = m;
  return { ticker, strike: parseInt(rawStrike) / 1000, side: cp === "C" ? "Call" : "Put", expDate: `${mm}/${dd}/${yy}` };
}

/**
 * QuickTradeModal — one-tap Sell (close) or Buy-More (add) for an existing position.
 *
 * Props:
 *   position: { symbol (OSI for options), ticker, quantity, lastPrice, avgCost, type }
 *   mode: "sell" | "buy_more"
 *   onClose: () => void
 *   onSuccess: (result) => void
 */
export default function QuickTradeModal({ position, mode, onClose, onSuccess, onModeChange, onViewChain }) {
  const isOption = String(position.type || "").includes("OPTION") || !!parseOCC(position.symbol);
  const isCrypto = String(position.type || "").includes("CRYPTO");
  const occ = isOption ? parseOCC(position.symbol) : null;
  const displayTicker = occ ? occ.ticker : position.ticker || position.symbol;
  const isSell = mode === "sell";
  // A position already funded by zBucks must close the same way it opened —
  // only a fresh BUY lets the user pick the funding source.
  const lockedFunding = isSell ? (position.fundingSource || "usd") : null;
  const [fundingSource, setFundingSource] = useState(lockedFunding || "usd");
  const [zbucksBalance, setZbucksBalance] = useState(null);
  const [buyingPower, setBuyingPower] = useState(null);
  useEffect(() => {
    if (lockedFunding) return; // sell of an existing zbucks position — nothing to fetch
    axios.get(`${API}/floor-chat/agents/zbucks`).then(r => {
      const board = r.data?.leaderboard || r.data?.agents || (Array.isArray(r.data) ? r.data : []);
      const me = board.find(a => a.agent_key === "user" || a.agentKey === "user");
      if (me) setZbucksBalance(me.zbucks_balance ?? me.zbucksBalance ?? null);
    }).catch(() => {});
    axios.get(`${API}/portfolio`).then(r => {
      const bp = isOption ? r.data?.optionsBuyingPower : r.data?.cashBuyingPower;
      setBuyingPower(bp ?? r.data?.buyingPower ?? null);
    }).catch(() => {});
  }, [lockedFunding, isOption]);

  // Round crypto held qty to 4 decimals to avoid JS float noise (e.g., 29.20000000000003 → 29.2)
  const heldQty = Math.abs(Number(position.quantity || 0)) || 0;
  const heldQtyDisplay = isCrypto ? Math.round(heldQty * 10000) / 10000 : heldQty;
  const [qty, setQty] = useState(isSell ? heldQtyDisplay : 1);
  const [qtyInput, setQtyInput] = useState(String(isSell ? heldQtyDisplay : 1));
  const [qtyFocused, setQtyFocused] = useState(false);
  const [livePrice, setLivePrice] = useState(position.lastPrice || 0);
  const [bid, setBid] = useState(null);
  const [ask, setAsk] = useState(null);
  // For the P/L chart + Zi Projection panel — the option's own greeks give
  // IV, but the underlying's own share price (not the contract's) is what
  // both of those actually plot against.
  const [iv, setIv] = useState(null);
  const [stockPrice, setStockPrice] = useState(null);
  const [projection, setProjection] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [err, setErr] = useState("");
  const [dryRun, setDryRun] = useState(false);
  const [dryPreview, setDryPreview] = useState(null);
  // iter 94 — swipe-to-confirm handshake. When user taps the main BUY/SELL
  // button, we open the ExecutionConfirmModal which shows fees/totals and
  // requires a slide gesture before placing the order.
  const [confirmOrder, setConfirmOrder] = useState(null);

  // Fetch live quote + bid/ask for the symbol
  useEffect(() => {
    const sym = position.symbol;
    if (!sym) return;
    const load = () => {
      if (isOption) {
        axios.get(`${API}/market/greeks?osi=${sym}`).then(r => {
          const d = r.data || {};
          if (d.mid != null) setLivePrice(d.mid);
          else if (d.bid != null && d.ask != null) setLivePrice((d.bid + d.ask) / 2);
          if (d.bid != null) setBid(d.bid);
          if (d.ask != null) setAsk(d.ask);
          if (d.iv != null) setIv(d.iv);
        }).catch(() => {});
      } else {
        // Equity + crypto both go via /market/quotes. Backend detects crypto and fetches from CRYPTO instrument type.
        axios.get(`${API}/market/quotes?symbols=${displayTicker}`).then(r => {
          const q = Array.isArray(r.data) ? r.data[0] : r.data;
          if (q?.price) setLivePrice(q.price);
          if (q?.bid != null) setBid(q.bid);
          if (q?.ask != null) setAsk(q.ask);
        }).catch(() => {});
      }
    };
    load();
    // Named pollId, not "iv" — this component also tracks implied
    // volatility in state under that name; shadowing it here would be a
    // confusing footgun for anyone reading the closure below.
    const pollId = setInterval(load, 3000);
    return () => clearInterval(pollId);
  }, [position.symbol, isOption, isCrypto, displayTicker]);

  // Underlying's own share price — the option's own mid (livePrice above)
  // is a different number, and both the P/L chart and Zi Projection need
  // the STOCK price, not the contract's.
  useEffect(() => {
    if (!isOption || !displayTicker) return;
    const load = () => axios.get(`${API}/market/quotes?symbols=${displayTicker}`).then(r => {
      const q = Array.isArray(r.data) ? r.data[0] : r.data;
      if (q?.price) setStockPrice(q.price);
    }).catch(() => {});
    load();
    const pollId = setInterval(load, 4000);
    return () => clearInterval(pollId);
  }, [isOption, displayTicker]);

  // Omega Zi Projection — same single-contract Monte Carlo endpoint the
  // fresh-buy flow uses (OptionsTradingFlow's ExecuteStep). Fires once per
  // contract once real inputs land; occ.expDate is "MM/DD/YY" from parseOCC.
  const dte = occ ? Math.max(0, Math.round((new Date(`20${occ.expDate.slice(6)}-${occ.expDate.slice(0, 2)}-${occ.expDate.slice(3, 5)}T16:00:00`) - new Date()) / 86400000)) : null;
  useEffect(() => {
    if (projection || !isOption || !occ || !stockPrice || !livePrice || !(iv > 0)) return;
    axios.post(`${API}/options/projection`, {
      ticker: displayTicker, strike: occ.strike, stockPrice, iv, mid: livePrice, dte: dte ?? 1,
      optionType: occ.side === "Put" ? "put" : "call",
    }).then(r => setProjection(r.data || null)).catch(() => {});
  }, [projection, isOption, occ, stockPrice, livePrice, iv, dte, displayTicker]);

  const totalCost = livePrice * qty * (isOption ? 100 : 1);
  const moneySym = fundingSource === "zbucks" ? "Ⓩ" : "$";
  const available = fundingSource === "zbucks" ? zbucksBalance : buyingPower;
  const perUnitCost = livePrice * (isOption ? 100 : 1);
  const maxAffordableQty = (available != null && perUnitCost > 0) ? Math.max(0, available / perUnitCost) : 0;
  const sideLabel = isSell ? "SELL" : "BUY";
  const title = isSell ? "Close Position" : "Buy More";
  const btnColor = isSell ? "#ef4444" : "#16a34a";
  const btnGlow = isSell ? "0 6px 20px rgba(239,68,68,0.35)" : "0 6px 20px rgba(22,163,74,0.35)";

  // Format displayed qty (crypto is fractional, options/equity are whole)
  const fmtQty = (q) => {
    if (isCrypto) return Number(q).toFixed(4).replace(/\.?0+$/, "");
    return String(Math.floor(Number(q)));
  };
  // Qty stepper increment (crypto fractional = 0.1 step, others = 1)
  const QTY_STEP = isCrypto ? 0.1 : 1;
  const QTY_MIN = isCrypto ? 0.0001 : 1;

  // Keep the free-text qty input in sync when qty changes via the +/- steppers
  // (or on mount) — but not while the user is actively typing in it, or every
  // keystroke would get clobbered by this effect re-formatting mid-edit.
  useEffect(() => {
    if (!qtyFocused) setQtyInput(fmtQty(qty));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty, qtyFocused]);

  const commitQtyInput = (raw) => {
    const n = parseFloat(raw);
    if (!raw || isNaN(n) || n <= 0) { setQtyInput(fmtQty(qty)); return; }
    const clampedMin = Math.max(QTY_MIN, n);
    const clamped = isSell ? Math.min(heldQtyDisplay, clampedMin) : clampedMin;
    const rounded = isCrypto ? Math.round(clamped * 10000) / 10000 : Math.floor(clamped);
    setQty(rounded);
    setQtyInput(fmtQty(rounded));
  };

  // Fill-to-max always rounds DOWN (never up) — rounding a max-affordable qty to
  // the nearest 0.0001 like commitQtyInput does can round it *up* past what's
  // actually available (e.g. true max 0.00055 coins rounds to 0.0006, which
  // costs more than the balance it was computed from), defeating the point of
  // a "max you can afford" button.
  const fillMax = (rawMax) => {
    if (!(rawMax > 0)) return;
    const floored = isCrypto ? Math.floor(rawMax * 10000) / 10000 : Math.floor(rawMax);
    if (floored < QTY_MIN) return;
    const clamped = isSell ? Math.min(heldQtyDisplay, floored) : floored;
    setQty(clamped);
    setQtyInput(fmtQty(clamped));
  };

  const place = async () => {
    hapticTap();
    setErr("");
    setDryPreview(null);

    // Normalize symbol once (backend also normalizes, keep client clean).
    const cleanSym = isCrypto && position.symbol?.endsWith("-USD") ? position.symbol.slice(0, -4) : position.symbol;

    // iter 97 — livePrice is already polled every 3s by the useEffect above,
    // so we trust it rather than round-tripping a fresh /market/option-quote
    // before opening the confirm modal. Saves ~200-400 ms on the critical path.
    const effectiveLimit = livePrice;

    // For dry-run we keep the legacy preflight flow so the user can see the
    // structured order-value preview. Real orders flow through the swipe-to-
    // confirm modal for fee disclosure.
    if (dryRun) {
      setPlacing(true);
      try {
        const body = {
          symbol: cleanSym, side: sideLabel, quantity: qty, limitPrice: livePrice,
          instrumentType: isOption ? "OPTION" : (isCrypto ? "CRYPTO" : "EQUITY"),
          orderType: "LIMIT", dryRun: true, fundingSource,
        };
        if (isOption) { body.osi = cleanSym; body.openClose = isSell ? "CLOSE" : "OPEN"; }
        const res = await axios.post(`${API}/order/place`, body);
        if (res.data?.ok === false) { hapticError(); setErr(res.data?.error || "Dry run failed"); }
        else { hapticSuccess(); setDryPreview(res.data.preflight || {}); }
      } catch (e) {
        hapticError();
        setErr(e.response?.data?.detail || e.message || "Dry run failed");
      }
      setPlacing(false);
      return;
    }

    // Refresh option mid-price right before the confirm modal loads — now
    // a no-op (see comment above). Kept variable names intact for downstream.

    // Auto-upscale crypto BUY to meet Public.com's $1 minimum.
    let effectiveQty = isCrypto ? Math.round(qty * 10000) / 10000 : qty;
    if (isCrypto && !isSell && effectiveQty * effectiveLimit < 1.0) {
      const decimals = cleanSym === "DOGE" ? 1 : 4;
      const factor = Math.pow(10, decimals);
      const minQty = Math.ceil((1.05 / effectiveLimit) * factor) / factor;
      if (minQty > effectiveQty) effectiveQty = minQty;
    }

    // Omega risk gate — only meaningful for real broker orders; a zbucks fill
    // never reaches the broker or the real account's risk limits.
    if (fundingSource !== "zbucks") {
      try {
        const riskRes = await axios.post(`${API}/omega/risk-check`, {
          symbol: position?.symbol || position?.ticker,
          side: mode === "sell" ? "SELL" : "BUY",
          quantity: qty || 1,
          price: position?.lastPrice || position?.optionPrice || 0,
        });
        if (riskRes.data?.blocked) {
          setErr(`Omega Risk Gate: ${riskRes.data.reason || "Trade blocked by risk committee"}`);
          return;
        }
      } catch { /* non-blocking — if endpoint fails, proceed */ }
    }

    setConfirmOrder({
      symbol: cleanSym,
      ticker: displayTicker,
      osi: isOption ? cleanSym : undefined,
      instrumentType: isOption ? "OPTION" : (isCrypto ? "CRYPTO" : "EQUITY"),
      side: sideLabel,
      quantity: effectiveQty,
      limitPrice: effectiveLimit,
      openClose: isOption ? (isSell ? "CLOSE" : "OPEN") : undefined,
      orderType: "LIMIT",
      fundingSource,
      label: `${displayTicker}${occ ? ` · $${occ.strike} ${occ.side}` : ""}${fundingSource === "zbucks" ? " · zBucks" : ""}`,
    });
  };

  const onConfirmed = (result) => {
    hapticSuccess();
    soundWin();
    onSuccess?.({
      success: true,
      orderId: result?.order?.orderId || result?.orderId,
      ...result,
      mode,
      ticker: displayTicker,
      symbol: position.symbol,
      quantity: confirmOrder?.quantity,
      limitPrice: confirmOrder?.limitPrice,
    });
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 10099, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      data-testid="quick-trade-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "min(420px, 100%)", background: "#0d1117", border: `1px solid ${btnColor}40`, borderRadius: 18, padding: 22,
          boxShadow: `0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px ${btnColor}22`,
          // Real bug, not cosmetic: the P/L chart + Zi Projection panel
          // added real height here, and this card had no scroll of its own —
          // centered in a flex box with no max-height, taller content just
          // overflows equally off the top AND bottom with nothing to scroll,
          // silently taking the Sell/Buy button with it off-screen.
          maxHeight: "calc(100dvh - 32px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
          overflowY: "auto", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: btnColor, letterSpacing: 1 }}>{isSell ? "QUICK CLOSE" : "ADD TO POSITION"}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#e6edf3", marginTop: 2 }}>{title}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="quick-trade-close"
            aria-label="Close"
            style={{
              width: 36, height: 36, borderRadius: 18,
              background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)",
              color: "#e6edf3", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        {/* Buy More / Sell toggle — only when the caller wants both reachable
            from one screen (SwipeCards3's position popup); remounts the
            whole modal on change via a `key` on the parent's side so every
            piece of state below (qty defaults, locked funding, etc.) reset
            cleanly for the new mode instead of getting patched piecemeal. */}
        {onModeChange && (
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {[["buy_more", "Buy More"], ["sell", "Sell"]].map(([id, label]) => (
              <button key={id} type="button" onClick={() => { if (id !== mode) { hapticTap(); onModeChange(id); } }} style={{
                flex: 1, padding: "10px 6px", borderRadius: 11, cursor: "pointer", fontSize: 12.5, fontWeight: 800,
                border: `1px solid ${mode === id ? btnColor : "rgba(255,255,255,0.14)"}`,
                background: mode === id ? `${btnColor}22` : "transparent",
                color: mode === id ? btnColor : "#8b949e",
              }}>{label}</button>
            ))}
          </div>
        )}

        {/* Contract */}
        <div style={{ padding: 14, borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", marginBottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#e6edf3" }}>{displayTicker}</div>
          {occ && (
            <div style={{ fontSize: 13, color: "#60a5fa", marginTop: 2 }}>${occ.strike} {occ.side} · Exp {occ.expDate}</div>
          )}
          <div style={{ fontSize: 10, color: "#636e7b", marginTop: 4, fontFamily: "monospace", wordBreak: "break-all" }}>{position.symbol}</div>
          <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 11, color: "#8b949e" }}>
            <span>Held: <strong style={{ color: "#e6edf3" }}>{fmtQty(heldQtyDisplay)}{isOption ? " contract" + (heldQtyDisplay !== 1 ? "s" : "") : isCrypto ? "" : " sh"}</strong></span>
            {position.avgCost != null && <span>Avg: <strong style={{ color: "#e6edf3" }}>${fN(position.avgCost, 4)}</strong></span>}
          </div>
        </div>

        {/* Live price */}
        <div style={{ textAlign: "center", padding: "14px 0", marginBottom: 14, borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
          <div style={{ fontSize: 10, color: "#636e7b", letterSpacing: 1, textTransform: "uppercase" }}>Live Mid Price</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: "#e6edf3", lineHeight: 1.1, marginTop: 4 }}>${fN(livePrice, isOption ? 2 : 4)}</div>
          {(bid != null || ask != null) && (
            <div style={{ fontSize: 11, color: "#636e7b", marginTop: 4 }}>Bid ${fN(bid, 2)} · Ask ${fN(ask, 2)}</div>
          )}
        </div>

        {/* P/L at expiration + Omega Zi Projection — same simulation the
            fresh-buy flow shows, now available on an existing position too. */}
        {isOption && occ && stockPrice > 0 && livePrice > 0 && (
          <PLChart
            contract={{ strike: occ.strike }} stockPrice={stockPrice} mid={livePrice} qty={qty || 1}
            isCall={occ.side !== "Put"} breakeven={occ.side === "Put" ? occ.strike - livePrice : occ.strike + livePrice}
          />
        )}
        {isOption && <ZiProjectionPanel projection={projection} stockPrice={stockPrice} strike={occ?.strike} />}
        {onViewChain && (
          <button type="button" onClick={() => onViewChain(displayTicker)} style={{
            width: "100%", padding: "10px", borderRadius: 11, cursor: "pointer", fontSize: 12, fontWeight: 800,
            border: "1px solid rgba(255,255,255,0.14)", background: "transparent", color: "#8b949e", marginBottom: 14,
          }}>View Full Chain</button>
        )}

        {/* Qty stepper */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 22, marginBottom: 14 }}>
          <button onClick={() => { hapticTap(); setQty(q => Math.max(QTY_MIN, Math.round((q - QTY_STEP) * 10000) / 10000)); }} data-testid="qt-minus" style={{ width: 42, height: 42, borderRadius: 21, background: "rgba(255,255,255,0.06)", border: "none", color: "#e6edf3", fontSize: 22, cursor: "pointer", fontWeight: 600 }}>−</button>
          <div style={{ textAlign: "center", minWidth: 90 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <span style={{ fontSize: 10, color: "#636e7b", textTransform: "uppercase", letterSpacing: 0.5 }}>{isOption ? "Contracts" : isCrypto ? "Coins" : "Shares"}</span>
              {!isSell && available != null && livePrice > 0 && (
                <button
                  type="button"
                  data-testid="qt-max"
                  onClick={() => { hapticTap(); fillMax(maxAffordableQty); }}
                  title={`Fill max affordable with ${fundingSource === "zbucks" ? "zBucks" : "buying power"} (${moneySym}${fN(available, 2)})`}
                  style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: 0.4, padding: "1px 6px", borderRadius: 5,
                    background: "rgba(22,163,74,0.14)", border: "1px solid rgba(22,163,74,0.4)", color: "#4ade80", cursor: "pointer",
                  }}
                >MAX</button>
              )}
            </div>
            <input
              type="text"
              inputMode="decimal"
              data-testid="qt-input"
              value={qtyInput}
              onFocus={(e) => { setQtyFocused(true); e.target.select(); }}
              onChange={(e) => {
                const v = e.target.value;
                // Allow only digits + a single decimal point while typing (crypto only needs
                // the decimal; shares/contracts still accept it but commitQtyInput floors it).
                if (v === "" || /^\d*\.?\d*$/.test(v)) setQtyInput(v);
              }}
              onBlur={(e) => { setQtyFocused(false); commitQtyInput(e.target.value); }}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              style={{
                width: 90, fontSize: 28, fontWeight: 800, color: "#e6edf3", background: "transparent",
                border: "none", borderBottom: `1px solid ${qtyFocused ? "#16a34a" : "transparent"}`,
                textAlign: "center", outline: "none", fontFamily: "inherit", padding: 0,
              }}
            />
            {isSell && qty === heldQtyDisplay && heldQtyDisplay > 0 && (
              <div style={{ fontSize: 9, color: "#f87171", marginTop: 2, fontWeight: 700 }}>CLOSE ALL</div>
            )}
          </div>
          <button onClick={() => { hapticTap(); setQty(q => isSell ? Math.min(heldQtyDisplay, Math.round((q + QTY_STEP) * 10000) / 10000) : Math.round((q + QTY_STEP) * 10000) / 10000); }} data-testid="qt-plus" disabled={isSell && qty >= heldQtyDisplay} style={{ width: 42, height: 42, borderRadius: 21, background: "rgba(255,255,255,0.06)", border: "none", color: "#e6edf3", fontSize: 22, cursor: (isSell && qty >= heldQtyDisplay) ? "default" : "pointer", fontWeight: 600, opacity: (isSell && qty >= heldQtyDisplay) ? 0.4 : 1 }}>+</button>
        </div>

        {/* Total */}
        <div style={{ textAlign: "center", fontSize: 13, color: "#636e7b", marginBottom: 14 }}>
          {isSell ? "Proceeds" : "Total Cost"}: <strong style={{ color: "#e6edf3", fontSize: 18, marginLeft: 4 }}>{moneySym}{fN(totalCost, 2)}</strong>
        </div>

        {err && (
          <div style={{ padding: 10, borderRadius: 8, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.24)", marginBottom: 12, fontSize: 11, color: "#f87171" }} data-testid="qt-error">{err}</div>
        )}

        {dryPreview && (
          <div data-testid="qt-dry-preview" style={{ padding: 10, borderRadius: 8, background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.24)", marginBottom: 12, fontSize: 11, color: "#93c5fd" }}>
            <div style={{ fontWeight: 700, marginBottom: 4, color: "#60a5fa" }}>✓ Dry Run Preflight</div>
            <div>Order value: <strong>{moneySym}{Number(dryPreview.order_value ?? 0).toFixed(2)}</strong></div>
            {dryPreview.estimated_commission != null && <div>Commission: <strong>{moneySym}{Number(dryPreview.estimated_commission).toFixed(2)}</strong></div>}
            {dryPreview.estimated_proceeds != null && <div>Est. proceeds: <strong>{moneySym}{Number(dryPreview.estimated_proceeds).toFixed(2)}</strong></div>}
            {dryPreview.buying_power_requirement != null && <div>BP required: <strong>{moneySym}{Number(dryPreview.buying_power_requirement).toFixed(2)}</strong></div>}
          </div>
        )}

        {/* Funding source — only choosable on a fresh buy; a sell must close
            the way the position opened (real broker vs. paper zbucks). */}
        {!lockedFunding && (
          <div data-testid="qt-funding-toggle" style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            {["usd", "zbucks"].map((src) => (
              <button key={src} type="button" data-testid={`qt-funding-${src}`}
                onClick={() => { hapticTap(); setFundingSource(src); setDryPreview(null); }}
                style={{
                  flex: 1, padding: "9px 6px", borderRadius: 10, cursor: "pointer",
                  border: `1px solid ${fundingSource === src ? btnColor : "rgba(255,255,255,0.12)"}`,
                  background: fundingSource === src ? `${btnColor}22` : "rgba(255,255,255,0.03)",
                  color: fundingSource === src ? "#e6edf3" : "#8b949e",
                  fontSize: 11.5, fontWeight: 800,
                }}>
                {src === "usd" ? "Real USD" : "zBucks (paper)"}
              </button>
            ))}
          </div>
        )}
        {fundingSource === "zbucks" && (
          <div style={{ textAlign: "center", fontSize: 10.5, color: totalCost > (zbucksBalance ?? Infinity) ? "#f87171" : "#636e7b", marginBottom: 10 }}>
            {zbucksBalance != null ? `zBucks balance: Ⓩ${fN(zbucksBalance, 2)}` : "Loading zBucks balance…"}
            {zbucksBalance != null && !isSell && totalCost > zbucksBalance && (
              <div style={{ marginTop: 2 }}>This will take you negative — a real loss, no floor at zero.</div>
            )}
          </div>
        )}
        {fundingSource === "usd" && !isSell && (
          <div style={{ textAlign: "center", fontSize: 10.5, color: totalCost > (buyingPower ?? Infinity) ? "#f87171" : "#636e7b", marginBottom: 10 }}>
            {buyingPower != null ? `Available: $${fN(buyingPower, 2)}` : "Loading buying power…"}
            {buyingPower != null && totalCost > buyingPower && (
              <div style={{ marginTop: 2 }}>Exceeds available buying power.</div>
            )}
          </div>
        )}

        {/* Dry Run toggle */}
        <label data-testid="qt-dry-run-toggle" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--dim)", marginBottom: 10, cursor: "pointer", justifyContent: "center" }}>
          <input type="checkbox" checked={dryRun} onChange={(e) => { setDryRun(e.target.checked); setDryPreview(null); }} style={{ accentColor: "#60a5fa", cursor: "pointer" }} />
          Dry Run · preflight-only (no order placed)
        </label>

        {/* Action */}
        <button
          onClick={place}
          disabled={placing || qty < QTY_MIN || (isSell && qty > heldQtyDisplay)}
          data-testid={`qt-${isSell ? "sell" : "buy-more"}-btn`}
          style={{
            width: "100%", padding: 14, borderRadius: 30,
            background: placing ? "#636e7b" : btnColor,
            color: "#fff", fontSize: 15, fontWeight: 700, border: "none",
            cursor: placing ? "default" : "pointer",
            boxShadow: placing ? "none" : btnGlow,
          }}
        >{placing ? "Placing…" : `${dryRun ? "DRY RUN · " : ""}${sideLabel} ${fmtQty(qty)}${isOption ? " contract" + (qty !== 1 ? "s" : "") : isCrypto ? "" : " sh"} · ${moneySym}${fN(totalCost, 2)}`}</button>
      </div>

      {confirmOrder && (
        <ExecutionConfirmModal
          order={confirmOrder}
          onClose={() => setConfirmOrder(null)}
          onConfirmed={(r) => { setConfirmOrder(null); onConfirmed(r); }}
        />
      )}
    </div>
  );
}
